import { AddressValidatedJsonldAddressValidatedRead } from '@/api/generated';
import { yupResolver } from '@hookform/resolvers/yup';
import useUserApi from 'hooks/UserApi/useUserApi';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

const addressSchema = () =>
  yup
    .object({
      countryCode: yup.string().required(),
      city: yup.string().required().max(100),
      address: yup.string().required().max(120),
      zipCode: yup.string().required().max(10),
      state: yup.string(),
    })
    .required();

type AddressFormValues = yup.InferType<ReturnType<typeof addressSchema>>;

const useAddressForm = ({
  validateAddress,
}: {
  validateAddress: ({
    values,
    countryCode,
    zipCode,
  }: {
    values: string;
    countryCode: string;
    zipCode: string;
  }) => Promise<AddressValidatedJsonldAddressValidatedRead>;
}) => {
  const formMethods = useForm<AddressFormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(addressSchema()),
  });
  const { handleSubmit, setError, formState } = formMethods;
  const { user } = useUserApi();

  const onSubmit = async ({ countryCode, zipCode, ...v }: AddressFormValues) => {
    const codeCountry = countryCode.split('/').pop();
    if (!codeCountry) {
      return;
    }

    const fieldOrder = ['address', 'city', 'state'];
    const values = fieldOrder.reduce((acc, field) => {
      const value = v[field as keyof Omit<AddressFormValues, 'countryCode' | 'zipCode'>];

      return value ? `${acc}, ${value}` : acc;
    }, '');

    const result = await validateAddress({ values, countryCode: codeCountry, zipCode });
    if (!result.isValid) {
      setError('countryCode', { message: '' });
      setError('city', { message: '' });
      setError('address', { message: '' });
      setError('zipCode', { message: '' });
      setError('state', { message: '' });
    }
  };

  useEffect(() => {
    if (user?.country && !formState.defaultValues) {
      formMethods.reset({
        countryCode: user.country,
      });
    }
  }, [user?.country, formState.defaultValues]);

  return { formMethods, onSubmit: handleSubmit(onSubmit), formState };
};

export default useAddressForm;
